// Dev Mode (docs/features/dev-mode.md — Josh, 10-01: "a switch that, inside the webxr app, I can just use dev mode
// and it gives me the latest javascript"): with it on, this browser loads the web app from a dev Mac — the copy
// inside its running app, or a folder it serves with live reload — instead of the site's deployed files.
//
// Its registration: the wire kind `devcode` (link.js) and `watchDevCode(mac, link)`, which app.js calls once a
// Mac's link is up. Only on staging and local hosts (gate.js); only for a Mac whose identity key is pinned; only
// when that Mac says it serves (a dev build — the release app has no such kind); and only when the person turns it
// on here, in the ⋯ sheet. Nothing the Mac sends can turn it on.
//
// The path of a file: the service worker (sw.js) → postMessage to this page → `get` over the Mac's wire link →
// the bytes back to the worker. Most files never take it: the text files are fetched ahead (sync.js), so a
// reload starts from them while the link is still coming up.
import './gate.js'
import { registerWire } from '../core/wire.js'
import { pins } from '../core/pairing.js'
import { logLine } from '../core/log.js'
import { APP_ROOT } from '../core/assets.js'
import * as sw from './switch.js'
import { hear, answered, getFile } from './link.js'
import { syncCopy, Refused } from './sync.js'
import { drawRow, drawBanner } from './view.js'

const enabled = sw.allowedHost(location.hostname)
const BASE = APP_ROOT.pathname
const offers = new Map()   // Mac key → { offer, mac, link }
let current = { on: false }        // the switch (localStorage)
let worker = { dev: false, state: null }   // what the service worker says: is this page the dev copy, and its state
let note = ''
let syncing = null, again = null   // a sync under way; what to do once more after it (a change heard meanwhile)
let reloadTimer = null, changeTimer = null

registerWire('devcode', (link, payload) => { if (enabled) heard(link, payload) })

/// A Mac's link is up (app.js): ask whether it serves the web app. Only a Mac whose identity key is pinned.
export function watchDevCode(mac, link) {
  if (!enabled || !mac?.room || !pins.get(mac.room)) return
  link.devcodeMac = mac
  link.send('devcode', { what: 'hello' })
}

function heard(link, payload) {
  const { message, body } = hear(payload)
  if (!message || answered(message, body)) return
  const mac = link.devcodeMac
  if (!mac) return
  if (message.what === 'offer') {
    if (!sw.mayOffer({ host: location.hostname, pinned: mac.room && pins.get(mac.room), offer: message })) return
    const first = !offers.has(mac.key)
    offers.set(mac.key, { offer: message, mac, link })
    if (first) logLine(`dev mode: ${mac.name} offers ${sw.sourceText(message, mac.name)}`)
    const act = sw.onOffer({ switchOn: current.on, chosen: current.mac, from: mac.key, servedDev: worker.dev, fallback: worker.state?.fallback })
    if (act === 'sync') sync({ reload: 'if-changed' })
    else if (act === 'sync-reload') sync({ reload: 'always' })
    draw()
  } else if (message.what === 'changed' && current.on && mac.key === current.mac && worker.dev) {
    clearTimeout(changeTimer)
    changeTimer = setTimeout(() => sync({ reload: 'if-changed' }), 300)
  }
}

// MARK: the service worker

function askWorker(message, wait = 4000) {
  const to = navigator.serviceWorker?.controller
  if (!to) return Promise.resolve(null)
  return new Promise((resolve) => {
    const channel = new MessageChannel()
    const timer = setTimeout(() => resolve(null), wait)
    channel.port1.onmessage = (e) => { clearTimeout(timer); resolve(e.data) }
    to.postMessage(message, [channel.port2])
  })
}

/// The worker asks for a file it does not hold yet: from the chosen Mac, if this page has its link.
async function fetchFor(event) {
  const port = event.ports?.[0]
  const entry = current.on && offers.get(current.mac)
  if (!port) return
  if (!entry) { port.postMessage({ status: 0 }); return }
  try {
    const got = await getFile(entry.link, String(event.data.path || ''))
    port.postMessage(got, [got.body])
  } catch (e) { port.postMessage({ status: 504, reason: e.message }) }
}

// MARK: sync, reload

async function sync({ reload }) {
  const entry = offers.get(current.mac)
  if (!entry) return false
  if (syncing) { again = reload; return syncing }
  syncing = (async () => {
    const wasReady = !!worker.state?.ready
    try {
      // the list is checked against the key this browser pinned for the Mac; the worker checks it again
      const pinned = pins.get(entry.mac.room)
      const { list, signed, changed } = await syncCopy({ link: entry.link, pinned, before: worker.state?.manifest || {}, base: BASE,
        onProgress: (done, total) => { if (total) { note = `fetching ${done}/${total}`; draw() } } })
      const took = await askWorker({ devcode: 'synced', mac: current.mac, signed: { ...signed, key: pinned }, offer: entry.offer })
      if (!took?.ok) throw new Refused(null, 'Dev code refused: the service worker could not check the file list')
      worker.state = { ...(worker.state || {}), on: true, ready: true, manifest: list.files, info: sw.signedInfo(list), offer: entry.offer, fallback: null }
      note = ''
      draw()
      if (reload === 'always' || (reload === 'if-changed' && (changed || !wasReady))) reloadSoon(worker.dev ? 'the Mac’s files changed' : 'the dev copy is ready')
      return true
    } catch (e) {
      if (e instanceof Refused) {
        // signed by someone else, or altered on the way: nothing of it is used — the site's copy, and say why
        note = ''
        logLine('dev mode: ' + e.message)
        await askWorker({ devcode: 'fallback', why: 'refused', reason: e.message })
        worker.state = { ...(worker.state || {}), fallback: { why: 'refused', reason: e.message, at: Date.now() } }
        draw()
        if (worker.dev) reloadSoon('dev code refused')
        return false
      }
      note = 'could not fetch: ' + e.message
      logLine('dev mode: ' + note)
      draw()
      return false
    } finally {
      syncing = null
      if (again) { const next = again; again = null; sync({ reload: next }) }
    }
  })()
  return syncing
}

/// Reload — but not over something being typed in the prompt box.
function reloadSoon(why) {
  clearTimeout(reloadTimer)
  reloadTimer = setTimeout(function go() {
    if (document.getElementById('prompt')?.value.trim()) { note = 'new code is ready — reloading once the prompt box is empty'; draw(); reloadTimer = setTimeout(go, 2000); return }
    logLine('dev mode: reloading — ' + why)
    location.reload()
  }, 300)
}

// MARK: the switch

async function turnOn(key) {
  const entry = offers.get(key)
  if (!entry) return
  if (!navigator.serviceWorker?.controller) { note = 'needs the app’s service worker — reload once, then turn it on'; draw(); return }
  current = { on: true, mac: key, macName: entry.mac.name }
  sw.writeSwitch(localStorage, current)
  await askWorker({ devcode: 'switch', ...current })
  worker = { dev: worker.dev, state: (await askWorker({ devcode: 'hello' }))?.state || null }
  logLine(`dev mode: on — ${sw.sourceText(entry.offer, entry.mac.name)}`)
  draw()
  await sync({ reload: 'always' })
}

async function turnOff() {
  current = { on: false }
  sw.writeSwitch(localStorage, current)
  await askWorker({ devcode: 'switch', on: false })
  for (const { link } of offers.values()) link.send('devcode', { what: 'bye' })
  logLine('dev mode: off')
  if (worker.dev) { location.replace(sw.withoutDevParam(location.href)); return }
  worker.state = null
  note = ''
  draw()
}

async function retry() {
  await askWorker({ devcode: 'retry' })
  worker.state = { ...(worker.state || {}), fallback: null }
  if (offers.has(current.mac)) await sync({ reload: 'always' })
  else location.reload()
}

// MARK: drawing

function draw() {
  drawRow([...offers.values()].map(({ offer, mac }) => ({ key: mac.key, label: sw.sourceText(offer, mac.name), on: current.on && current.mac === mac.key })),
    (key, on) => (on ? turnOn(key) : turnOff()))
  if (!current.on) { drawBanner(null); return }
  const entry = offers.get(current.mac)
  const name = entry?.mac.name || current.macName || 'the Mac'
  const off = { label: 'Off', act: turnOff }
  const fallback = worker.state?.fallback
  if (worker.dev) drawBanner({ text: sw.stripText(worker.state?.info, name), note, buttons: [off] })
  else if (fallback) drawBanner({ text: fallback.why === 'refused' ? fallback.reason || 'Dev code refused' : fallback.why === 'did-not-start' ? `DEV · the web app from ${name} didn’t start — this is the site’s` : `DEV · ${name} isn’t reachable — this is the site’s web app`, note, buttons: [{ label: 'Retry', act: retry }, off] })
  else drawBanner({ text: entry ? `DEV · getting the web app from ${name}` : `DEV · waiting for ${name}`, note, buttons: [off] })
}

// MARK: start

async function start() {
  if (sw.urlSaysOff(location.search)) {
    sw.writeSwitch(localStorage, { on: false })
    history.replaceState(history.state, '', sw.withoutDevParam(location.href))
    logLine('dev mode: off (?dev=off)')
  }
  current = sw.readSwitch(localStorage)
  navigator.serviceWorker?.addEventListener('message', (e) => {
    if (e.data?.devcode === 'fetch') fetchFor(e)
    else if (e.data?.devcode === 'missed') { note = `${e.data.path} came from the site`; draw() }
    else if (e.data?.devcode === 'refused') {
      // a file whose bytes were not the signed ones: the worker gave the site's instead and the next load is the site's
      note = e.data.reason || `Dev code refused: ${e.data.path}`
      logLine('dev mode: ' + note)
      draw()
    }
  })
  const hello = await askWorker({ devcode: 'hello' })
  worker = { dev: !!hello?.dev, state: hello?.state || null }
  // the page's switch is the truth; the worker only mirrors it
  if (current.on && (!worker.state?.on || worker.state.mac !== current.mac)) await askWorker({ devcode: 'switch', ...current })
  if (!current.on && worker.state?.on) await askWorker({ devcode: 'switch', on: false })
  if (sw.onLoad({ switchOn: current.on, servedDev: worker.dev }) === 'reload') { location.reload(); return }
  if (worker.dev) {
    await askWorker({ devcode: 'booted' })
    logLine(`dev mode: this page is ${sw.stripText(worker.state?.info, current.macName)}`)
    setTimeout(async () => {
      if (sw.onTimeout({ switchOn: current.on, servedDev: worker.dev, offered: offers.has(current.mac) }) !== 'fallback') return
      logLine(`dev mode: ${current.macName} did not answer in ${sw.OFFER_WAIT / 1000} s — loading the site’s web app`)
      await askWorker({ devcode: 'fallback', why: 'unreachable' })
      location.reload()
    }, sw.OFFER_WAIT)
  }
  draw()
}
if (enabled) start()
